

/**
 * AppTabs — Tabs integrados al header (Option C - Unified)
 *
 * Desktop: all tabs visible side by side
 * Mobile (<768px) with 2+ tabs: show only active tab with ◀ ▶ arrows
 *
 * - Click activa la app
 * - Hover: ícono de app cambia a pop-out (⧉), aparece X a la derecha
 * - Active tab tiene underline naranja
 */

import { useState, useEffect } from 'react'
import { useShellStore } from '@/store/shell-store'
import { colors, typography, spacing, borderRadius, transitions } from '@/lib/tokens'

const MOBILE_BREAKPOINT = 768

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

function TabItem({
  tab,
  isActive,
  onActivate,
  onClose,
  onPopout,
  getAppIcon,
}: {
  tab: { tabId: string; appId: string; title: string }
  isActive: boolean
  onActivate: () => void
  onClose: () => void
  onPopout: () => void
  getAppIcon: (appId: string) => string
}) {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        paddingLeft: spacing.sm,
        paddingRight: spacing.sm,
        paddingTop: '2px',
        minWidth: '80px',
        cursor: 'pointer',
        background: isActive
          ? colors.cardHover
          : isHovered
            ? `${colors.cardHover}80`
            : 'transparent',
        borderRadius: `${borderRadius.sm} ${borderRadius.sm} 0 0`,
        borderBottom: `2px solid ${isActive ? colors.primary : 'transparent'}`,
        transition: transitions.fast,
        userSelect: 'none',
        height: '100%',
      }}
      onClick={onActivate}
    >
      {/* Icon — on hover becomes pop-out button */}
      {isHovered ? (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPopout()
          }}
          title="Pop-Out"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            padding: 0,
            background: 'transparent',
            border: 'none',
            borderRadius: borderRadius.sm,
            cursor: 'pointer',
            color: colors.textSecondary,
            fontSize: '14px',
            lineHeight: 1,
            transition: transitions.fast,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = colors.primary
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.textSecondary
          }}
        >
          ⧉
        </button>
      ) : (
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          flexShrink: 0,
        }}>
          {(() => {
            const icon = getAppIcon(tab.appId)
            return icon.startsWith('/') ? (
              <img src={icon} alt="" style={{ width: 16, height: 16 }} />
            ) : (
              <span style={{ fontSize: '14px', lineHeight: 1 }}>{icon}</span>
            )
          })()}
        </span>
      )}

      {/* Title */}
      <span style={{
        flex: 1,
        fontSize: typography.fontSize.sm,
        fontFamily: typography.fontFamily.sans,
        fontWeight: isActive
          ? typography.fontWeight.medium
          : typography.fontWeight.normal,
        color: isActive ? colors.textPrimary : colors.textSecondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {tab.title}
      </span>

      {/* Close — only on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        title="Cerrar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          padding: 0,
          background: 'transparent',
          border: 'none',
          borderRadius: borderRadius.sm,
          cursor: 'pointer',
          color: colors.textSecondary,
          fontSize: '12px',
          lineHeight: 1,
          transition: transitions.fast,
          flexShrink: 0,
          opacity: isHovered ? 1 : 0,
          pointerEvents: isHovered ? 'auto' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = colors.error
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = colors.textSecondary
        }}
      >
        ✕
      </button>
    </div>
  )
}

function NavArrow({ direction, onClick }: { direction: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '100%',
        background: 'transparent',
        border: 'none',
        color: colors.textSecondary,
        fontSize: '12px',
        cursor: 'pointer',
        transition: transitions.fast,
        flexShrink: 0,
        padding: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = colors.textPrimary }}
      onMouseLeave={(e) => { e.currentTarget.style.color = colors.textSecondary }}
    >
      {direction === 'left' ? '◀' : '▶'}
    </button>
  )
}

export function AppTabs() {
  const { openTabs, activeTabId, setActiveTab, closeTab, openPopout, availableApps } = useShellStore()
  const isMobile = useIsMobile()

  const getAppIcon = (appId: string) => {
    const app = availableApps.find(a => a.appId === appId)
    return app?.icon || '📱'
  }

  const activeIndex = openTabs.findIndex(t => t.tabId === activeTabId)

  const goToPrev = () => {
    const prev = activeIndex > 0 ? activeIndex - 1 : openTabs.length - 1
    setActiveTab(openTabs[prev].tabId)
  }

  const goToNext = () => {
    const next = activeIndex < openTabs.length - 1 ? activeIndex + 1 : 0
    setActiveTab(openTabs[next].tabId)
  }

  if (openTabs.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        paddingLeft: spacing.md,
        color: colors.textMuted,
        fontSize: typography.fontSize.sm,
        fontFamily: typography.fontFamily.sans,
      }}>
        Sin apps abiertas
      </div>
    )
  }

  // Mobile with 2+ tabs: show only active tab with arrows
  if (isMobile && openTabs.length >= 2) {
    const activeTab = openTabs.find(t => t.tabId === activeTabId)
    if (!activeTab) return null

    return (
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        height: '100%',
        overflow: 'hidden',
      }}>
        <NavArrow direction="left" onClick={goToPrev} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', overflow: 'hidden' }}>
          <TabItem
            tab={activeTab}
            isActive={true}
            onActivate={() => {}}
            onClose={() => closeTab(activeTab.tabId)}
            onPopout={() => openPopout(activeTab.tabId)}
            getAppIcon={getAppIcon}
          />
        </div>
        <NavArrow direction="right" onClick={goToNext} />
        {/* Tab counter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          paddingRight: spacing.xs,
          fontSize: '9px',
          fontFamily: typography.fontFamily.mono,
          color: colors.textMuted,
          flexShrink: 0,
        }}>
          {activeIndex + 1}/{openTabs.length}
        </div>
      </div>
    )
  }

  // Desktop: all tabs visible
  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      height: '100%',
      gap: '2px',
      overflow: 'hidden',
    }}>
      {openTabs.map((tab) => (
        <TabItem
          key={tab.tabId}
          tab={tab}
          isActive={tab.tabId === activeTabId}
          onActivate={() => setActiveTab(tab.tabId)}
          onClose={() => closeTab(tab.tabId)}
          onPopout={() => openPopout(tab.tabId)}
          getAppIcon={getAppIcon}
        />
      ))}
    </div>
  )
}
